import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PROJECT_MANAGEMENT_URL } from '@insite-store360/shared';
import { Observable, catchError } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

export interface CostPreparationItem {
  id?: number;
  slNo: number;
  pnc: string;
  model: string;
  isManual?: string;
  brand: string;
  category: string;
  subCategory: string;
  retailSellingPrice: number | null;
  projectMinimumPrice: number | null;
  installationCost: number | null;
  stk1: number | null;
  otherLandedCost: number | null;
  costAboveGp: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class CostPreparationService {
  private readonly URL_PATH = `${PROJECT_MANAGEMENT_URL}/cost-preparation`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  /**
   * Get all cost preparation data with pagination, search, and filters
   */
  public getAll(
    pageIndex?: number,
    pageSize?: number,
    searchValue?: string,
    countryId?: number,
    categoryNames?: string[],
    subCategoryNames?: string[]
  ): Observable<{ 
    data: CostPreparationItem[]; 
    paginatorValue: { totalData: number; totalCount: number } 
  }> {
    let params = new HttpParams();
    if (pageIndex !== undefined && pageIndex !== null) {
      params = params.set('pageIndex', pageIndex.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (searchValue && searchValue.trim() !== '') {
      params = params.set('searchValue', searchValue.trim());
    }
    if (countryId !== undefined && countryId !== null) {
      params = params.set('countryId', countryId.toString());
    }
    if (categoryNames && categoryNames.length > 0) {
      categoryNames.forEach(name => {
        params = params.append('categoryNames', name);
      });
    }
    if (subCategoryNames && subCategoryNames.length > 0) {
      subCategoryNames.forEach(name => {
        params = params.append('subCategoryNames', name);
      });
    }
    return this.http
      .get<{ 
        data: CostPreparationItem[]; 
        paginatorValue: { totalData: number; totalCount: number } 
      }>(`${this.URL_PATH}/getAll`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Export cost preparation data to Excel
   */
  public exportExcel(
    searchValue?: string,
    countryId?: number
  ): Observable<Blob> {
    let params = new HttpParams();
    if (searchValue && searchValue.trim() !== '') {
      params = params.set('searchValue', searchValue.trim());
    }
    if (countryId !== undefined && countryId !== null) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .get<Blob>(`${this.URL_PATH}/downloadExcelReport`, {
        params,
        responseType: 'blob' as 'json'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Download Excel template with products from v_productlist
   */
  public downloadTemplate(countryId: number): Observable<Blob> {
    if (!countryId) {
      throw new Error('Country ID is required to download template');
    }
    const params = new HttpParams().set('countryId', countryId.toString());
    return this.http
      .get<Blob>(`${this.URL_PATH}/downloadExcelTemplate`, {
        params,
        responseType: 'blob' as 'json'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Import cost preparation data from Excel
   */
  public importData(
    data: any[],
    countryId?: number,
    excelFileName?: string,
    excelFileUrl?: string
  ): Observable<any> {
    let params = new HttpParams();
    if (countryId !== undefined && countryId !== null) {
      params = params.set('countryId', countryId.toString());
    }
    if (excelFileName) {
      params = params.set('excelFileName', excelFileName);
    }
    if (excelFileUrl) {
      params = params.set('excelFileUrl', excelFileUrl);
    }
    return this.http
      .post<any>(`${this.URL_PATH}/importData`, data, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get cost preparation activity logs with pagination, search, and country filters
   */
  public getLogs(
    pageIndex?: number,
    pageSize?: number,
    searchValue?: string,
    countryId?: number
  ): Observable<{
    data: any[];
    paginatorValue: { totalData: number; totalCount: number }
  }> {
    let params = new HttpParams();
    if (pageIndex !== undefined && pageIndex !== null) {
      params = params.set('pageIndex', pageIndex.toString());
    }
    if (pageSize !== undefined && pageSize !== null) {
      params = params.set('pageSize', pageSize.toString());
    }
    if (searchValue && searchValue.trim() !== '') {
      params = params.set('searchValue', searchValue.trim());
    }
    if (countryId !== undefined && countryId !== null) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .get<{
        data: any[];
        paginatorValue: { totalData: number; totalCount: number }
      }>(`${this.URL_PATH}/logs`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Export cost preparation activity logs to Excel
   */
  public exportLogsExcel(
    searchValue?: string,
    countryId?: number,
    updatedBy?: number,
    updatedDateTime?: string
  ): Observable<Blob> {
    let params = new HttpParams();
    if (searchValue && searchValue.trim() !== '') {
      params = params.set('searchValue', searchValue.trim());
    }
    if (countryId !== undefined && countryId !== null) {
      params = params.set('countryId', countryId.toString());
    }
    if (updatedBy !== undefined && updatedBy !== null) {
      params = params.set('updatedBy', updatedBy.toString());
    }
    if (updatedDateTime && updatedDateTime.trim() !== '') {
      params = params.set('updatedDateTime', updatedDateTime);
    }
    params = params.set('isLogs', 'true');
    return this.http
      .get<Blob>(`${this.URL_PATH}/downloadExcelReport`, {
        params,
        responseType: 'blob' as 'json'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }
}

